import axios from 'axios'

// 创建axios实例
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,  // 增加到30秒（大部分API请求足够了）
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  config => {
    // 从localStorage获取JWT令牌
    const token = localStorage.getItem('token')
    if (token) {
      // 将令牌添加到请求头
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => {
    return response.data
  },
  error => {
    console.error('API错误:', error)
    console.error('错误详情:', {
      message: error.message,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data
    })
    
    if (error.response && error.response.status === 401) {
      // 未授权，重定向到登录页
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      window.location.href = '/login'
    }
    
    // 确保错误对象包含完整的错误信息
    if (error.response && error.response.data) {
      error.message = error.response.data.message || error.message
    }
    
    return Promise.reject(error)
  }
)

// 认证API
export const authApi = {
  // 登录
  login(username, password) {
    return api.post('/auth/login', { username, password })
      .then(response => {
        // 检查响应格式
        if (response.success && response.data) {
          // 新的响应格式
          const data = response.data;
          if (data.token) {
            localStorage.setItem('token', data.token)
            // username may be at data.username or data.user.username
            const username = data.username || (data.user && data.user.username) || ''
            localStorage.setItem('username', username)
            const userId = data.userId || (data.user && data.user.id) || ''
            if (userId) {
              localStorage.setItem('userId', userId)
            }
            const realName = data.realName || (data.user && data.user.realName) || ''
            if (realName) {
              localStorage.setItem('realName', realName)
            }
            const roleIds = data.roleIds || (data.user && data.user.roleIds) || null
            if (roleIds) {
              localStorage.setItem('roleIds', JSON.stringify(roleIds))
            }
          }
          return data;
        } else if (response.token) {
          // 旧的响应格式（兼容性）
          localStorage.setItem('token', response.token)
          localStorage.setItem('username', response.username)
          return response;
        } else {
          throw new Error('登录响应格式错误');
        }
      })
  },
  
  // 检查是否已认证
  isAuthenticated() {
    return !!localStorage.getItem('token')
  },
  
  // 登出
  logout() {
    localStorage.removeItem('token')
    localStorage.removeItem('username')
    localStorage.removeItem('userId')
    localStorage.removeItem('realName')
    localStorage.removeItem('roleIds')
    return Promise.resolve()
  }
}

// Stats API
export const statsApi = {
  getStats() {
    return api.get('/stats');
  },
  getDashboardData() {
    return api.get('/stats/dashboard');
  },
  getStatsData() {
    return api.get('/stats/statsData');
  },
  // 获取图表数据
  getChartData(timeRange) {
    return api.get(`/stats/charts?timeRange=${timeRange}`);
  }
};

// Import API
export const importApi = {
  importData(data) {
    return api.post('/import', data);
  }
};

// Player API
export const playerApi = {
  getAllPlayers(params = {}) {
    // 支持传递额外的查询参数，如 gameType
    const queryString = Object.keys(params).length > 0 
      ? '?' + new URLSearchParams(params).toString() 
      : '';
    return api.get(`/players${queryString}`);
  },
  getPlayerById(id, params = {}) {
    // 支持传递额外的查询参数，如 gameType
    const queryString = Object.keys(params).length > 0 
      ? '?' + new URLSearchParams(params).toString() 
      : '';
    return api.get(`/players/${id}${queryString}`);
  },
  createPlayer(player) {
    return api.post('/players', player);
  },
  updatePlayer(id, player) {
    return api.put(`/players/${id}`, player);
  },
  deletePlayer(id) {
    return api.delete(`/players/${id}`);
  },
  // 别名管理API
  getPlayerAliases(playerId) {
    return api.get(`/players/${playerId}/aliases`);
  },
  addPlayerAlias(playerId, alias, source = '手动添加') {
    return api.post(`/players/${playerId}/aliases`, { alias, source });
  },
  deletePlayerAlias(playerId, aliasId) {
    return api.delete(`/players/${playerId}/aliases/${aliasId}`);
  },
  findPlayerByNameOrAlias(name) {
    return api.get(`/players/find-by-name-or-alias?name=${encodeURIComponent(name)}`);
  }
};

// Admin API
export const adminApi = {
  getAdminData() {
    return api.get('/admin');
  }
};

// User API
export const userApi = {
  getAllUsers() {
    return api.get('/users');
  },
  getUserById(id) {
    return api.get(`/users/${id}`);
  },
  createUser(user) {
    return api.post('/users', user);
  },
  updateUser(id, user) {
    return api.put(`/users/${id}`, user);
  },
  updatePassword(id, newPassword) {
    return api.put(`/users/${id}/password`, { newPassword });
  },
  updateStatus(id, status) {
    return api.put(`/users/${id}/status`, { status });
  },
  deleteUser(id) {
    return api.delete(`/users/${id}`);
  },
  assignRoles(userId, roleIds) {
    return api.post(`/users/${userId}/roles`, { roleIds });
  },
  getUserRoles(userId) {
    return api.get(`/users/${userId}/roles`);
  }
};

// Role API
export const roleApi = {
  getAllRoles() {
    return api.get('/roles');
  },
  getRoleById(id) {
    return api.get(`/roles/${id}`);
  },
  createRole(role) {
    return api.post('/roles', role);
  },
  updateRole(id, role) {
    return api.put(`/roles/${id}`, role);
  },
  updateStatus(id, status) {
    return api.put(`/roles/${id}/status`, { status });
  },
  deleteRole(id) {
    return api.delete(`/roles/${id}`);
  },
  assignMenus(roleId, menuIds) {
    return api.post(`/roles/${roleId}/menus`, { menuIds });
  },
  getRoleMenus(roleId) {
    return api.get(`/roles/${roleId}/menus`);
  }
};

// Menu API
export const menuApi = {
  getAllMenus() {
    return api.get('/menus');
  },
  getMenuById(id) {
    return api.get(`/menus/${id}`);
  },
  getMenusByParentId(parentId) {
    return api.get(`/menus/parent/${parentId}`);
  },
  getUserMenus(userId) {
    return api.get(`/menus/user/${userId}`);
  },
  createMenu(menu) {
    return api.post('/menus', menu);
  },
  updateMenu(id, menu) {
    return api.put(`/menus/${id}`, menu);
  },
  updateStatus(id, status) {
    return api.put(`/menus/${id}/status`, { status });
  },
  deleteMenu(id) {
    return api.delete(`/menus/${id}`);
  }
};

// Game Session API
export const sessionApi = {
  
  getAllSessions() {
    return api.get('/sessions');
  },
  getSessionById(id) {
    return api.get(`/sessions/${id}`);
  },
  createSession(session) {
    return api.post('/sessions', session);
  },
  updateSession(id, session) {
    return api.put(`/sessions/${id}`, session);
  },
  deleteSession(id) {
    return api.delete(`/sessions/${id}`);
  },
  // 更新牌局结果
  updateSessionResults(sessionId, data) {
    return api.put(`/sessions/${sessionId}/results`, data);
  },
  // 导入会话数据
  importSessions(file) {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/sessions/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  // 获取进行中的牌局详情
  getActiveSession(sessionId) {
    return api.get(`/sessions/active/${sessionId}`)
  },
  // 添加玩家到牌局
  addSessionPlayer(sessionId, playerData) {
    return api.post(`/sessions/${sessionId}/players`, playerData)
  },
  // 更新牌局中的玩家信息
  updateSessionPlayer(sessionId, playerData) {
    return api.put(`/sessions/${sessionId}/players/${playerData.playerId}`, playerData)
  },
  // 从牌局中移除玩家
  removeSessionPlayer(sessionId, playerId) {
    return api.delete(`/sessions/${sessionId}/players/${playerId}`)
  },
  // 结束牌局
  endSession(sessionId, data) {
    return api.post(`/sessions/${sessionId}/end`, data)
  },
  startSession(data) {
    return api.post('/sessions/start', data)
  },
  // 获取进行中的牌局
  getActiveSessions() {
    return api.get('/sessions/active')
  }
};

// 干瞪眼相关API
export const ganDengYanAPI = {
  // 解析并保存干瞪眼会话
  parseAndSaveSession: (data) => {
    return api.post('/gan-deng-yan/parse-and-save', data);
  },

  // 获取所有干瞪眼会话
  getAllSessions: () => {
    return api.get('/gan-deng-yan/sessions');
  },

  // 根据ID获取干瞪眼会话详情
  getSessionById: (sessionId) => {
    return api.get(`/gan-deng-yan/sessions/${sessionId}`);
  },

  // 删除干瞪眼会话
  deleteSession: (sessionId) => {
    return api.delete(`/gan-deng-yan/sessions/${sessionId}`);
  },

  // 获取玩家统计信息
  getPlayerStats: (playerName) => {
    return api.get(`/gan-deng-yan/player-stats/${playerName}`);
  },

  // 获取总体统计信息
  getOverallStats: () => {
    return api.get('/gan-deng-yan/overall-stats');
  },

  // 更新玩家支付状态
  updatePaymentStatus: (playerResultId, isPaid) => {
    return api.put(`/gan-deng-yan/player-results/${playerResultId}/payment-status`, { isPaid });
  },

  // 获取支付统计信息
  getPaymentStats: () => {
    return api.get('/gan-deng-yan/payment-stats');
  }
};

// 导出API实例
export default api 